import {useEffect, useRef, useState} from 'react';
import type {Props} from './types';
import {Box, Chip, Typography, Select, useTheme, MenuItem} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {ChevronDown, WhiteCrossOutlined} from '@jobseeker/lotus/icons';
import {SearchInput, VersionVariants} from '@jobseeker/lotus/elements';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';

const SelectIcon = ({className}: any) => {
  const theme = useTheme();
  return (
    <Image
      className={className}
      src={ChevronDown}
      alt={'Drop Down Arrow'}
      width={12}
      height={12}
      style={{
        userSelect: 'none',
        padding: '2px',
        zIndex: 0,
        marginRight: theme.spacing(1.7),
        marginLeft: theme.spacing(1.7),
        marginTop: theme.spacing(0.4),
        transition: '0.3s ease-in-out',
      }}
      objectFit="cover"
    />
  );
};

const ChooseChips = (props: Props) => {
  const theme = useTheme();
  const {
    selected,
    options,
    chooseType,
    placeholder,
    onSelect,
    deleteChip,
    maxCharLength,
    setDialogOpen,
    name,
  } = props;
  const [openSelect, setOpenSelect] = useState(false);
  const [searchText, setSearchText] = useState('');
  const chipBoxRef = useRef<HTMLDivElement>(null);
  const [chooserFocused, setChooserFocused] = useState(false);
  const isLarge = useIsLargeDevice();

  // Scroll to the bottom of the chip container when selected changes
  useEffect(() => {
    if (chipBoxRef.current) {
      chipBoxRef.current.scrollTop = chipBoxRef.current.scrollHeight;
    }
  }, [selected]);

  return (
    <Box
      height={'fit-content'}
      // maxHeight={130}
      width={'100%'}
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'space-between'}
      alignItems={'flex-start'}
      border={'1px solid'}
      borderColor={chooserFocused ? '#33333390' : '#7E7E7E33'}
      borderRadius={selected?.length ? 3.75 : 12.5}
      sx={{
        ':hover': {
          borderColor: '#33333390',
        },
      }}
    >
      <Box
        display={selected?.length ? 'flex' : 'none'}
        flexWrap={'wrap'}
        height={'fit-content'}
        // maxHeight={84}
        width={'100%'}
        gap={1.25}
        px={1.125}
        py={0.875}
        onClick={() => {
          if (!isLarge) {
            setDialogOpen(name);
          }
        }}
        // sx={{
        //   overflowY: 'auto',
        // }}
        ref={chipBoxRef}
      >
        {selected.map((chipValue: string | number, index: number) => {
          return (
            <Chip
              key={index}
              label={
                <Typography
                  display={'flex'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  gap={0.875}
                  variant="body2"
                  lineHeight={'18px'}
                >
                  {options.find(option => option.value == chipValue)?.label ??
                    chipValue}
                  <Image
                    src={WhiteCrossOutlined}
                    width={12}
                    height={12}
                    alt=""
                    style={{
                      cursor: 'pointer',
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      deleteChip(chipValue);
                    }}
                  />
                </Typography>
              }
              sx={{
                height: 30,
                backgroundColor: !isAppIimjobs
                  ? theme.palette.primary.main
                  : '#333',
              }}
              variant="filled"
              color="secondary"
            />
          );
        })}
      </Box>
      {chooseType === 'select' ? (
        <Select
          multiple
          displayEmpty
          open={openSelect}
          onOpen={() => {
            if (isLarge) {
              setOpenSelect(true);
            } else setDialogOpen(name);
          }}
          onClose={() => setOpenSelect(false)}
          value={selected}
          onFocus={() => {
            setChooserFocused(true);
          }}
          onBlur={() => {
            setChooserFocused(false);
          }}
          sx={{
            width: '100%',
            height: '40px',
            borderRadius: 8,
            '&.MuiOutlinedInput-root': {
              '& fieldset': {
                border: 0,
              },
              '&:hover fieldset': {
                border: 0,
              },
              '&.Mui-focused fieldset': {
                border: 0,
              },
              '&.Mui-error fieldset': {
                border: 0,
              },
            },
          }}
          IconComponent={({...iconProps}) => (
            <SelectIcon {...iconProps} open={openSelect} />
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.12)',
                borderRadius: '8px',
                marginTop: '5.6px',
              },
            },
            disableScrollLock: true,
            // marginThreshold: null,
            // disablePortal: true,
          }}
          onChange={e => {
            onSelect && onSelect(e);
            setOpenSelect(false);
          }}
          renderValue={() => {
            return (
              <Typography color="secondary" variant="subtitle2">
                {placeholder}
              </Typography>
            );
          }}
        >
          {options.map(option => (
            <MenuItem
              key={option.value}
              value={option.value.toString()}
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: 1.5,
                '&.Mui-selected': {
                  fontWeight: 500,
                  backgroundColor: isAppIimjobs
                    ? '#91B87929'
                    : 'rgba(233, 99, 12, 0.08)',
                },
                ':hover': {
                  backgroundColor: isAppIimjobs
                    ? '#91B87929'
                    : 'rgba(233, 99, 12, 0.08)',
                },
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      ) : chooseType === 'search' ? (
        <SearchInput
          options={options}
          placeholder={placeholder}
          {...(!isAppIimjobs && {hiristColor: true})}
          variant={VersionVariants.VERSION_TWO}
          formikName={name}
          value={searchText}
          name={'searchText'}
          key={'searchText'}
          maxCharLength={maxCharLength}
          setDialogOpen={setDialogOpen}
          onChange={e => {
            setSearchText(e.target.value);
          }}
          onSelect={e => {
            onSelect && onSelect(e);
            setSearchText('');
          }}
          onFocus={() => {
            setChooserFocused(true);
          }}
          onBlur={() => {
            setChooserFocused(false);
          }}
        />
      ) : (
        <></>
      )}
    </Box>
  );
};

export default ChooseChips;
